import React, { useState, useEffect } from 'react';
import {
  RefreshCw, Loader2, Zap, Shield, ShieldOff, RotateCcw, Cpu, Gamepad2,
  Activity, Server, Brain, Workflow
} from 'lucide-react';
import { ServiceHealth, SystemHealth } from '../../hooks/useHealthMonitor';
import { useSynapseWorker } from '../../hooks/useSynapseWorker';

interface StatusHeaderProps {
  sseConnected: boolean;
  wsConnected: boolean;
  supabaseConnected: boolean;
  onReconnect: () => void;
  onOpenProcessManager?: () => void;
  onOpenZomboidServices?: () => void;
  serviceHealth?: Record<string, ServiceHealth>;
  systemHealth?: SystemHealth | null;
}

const LOG_SERVER = '/api/claude-logs';

export const StatusHeader: React.FC<StatusHeaderProps> = ({
  sseConnected,
  wsConnected,
  supabaseConnected,
  onReconnect,
  onOpenProcessManager,
  onOpenZomboidServices,
  serviceHealth,
  systemHealth
}) => {
  const [runningCommand, setRunningCommand] = useState<string | null>(null);
  const [preToolEnabled, setPreToolEnabled] = useState<boolean | null>(null);
  const [systemRestarting, setSystemRestarting] = useState(false);
  const [mpcStatus, setMpcStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [synapseStatus, setSynapseStatus] = useState<'online' | 'offline' | 'checking'>('checking');

  // Worker-shell integration for Synapse health
  const { api: synapseApi, isReady: workerReady } = useSynapseWorker();

  // Check Synapse health via worker shell
  const checkSynapseHealth = async () => {
    if (!workerReady) {
      setSynapseStatus('checking');
      return;
    }
    try {
      const result = await synapseApi.checkHealth();
      setSynapseStatus(result.status === 'healthy' ? 'online' : 'offline');
    } catch {
      setSynapseStatus('offline');
    }
  };

  // Toggle Synapse service
  const toggleSynapse = async () => {
    setRunningCommand('synapse');
    try {
      const action = synapseStatus === 'online' ? 'stop' : 'start';
      await fetch(`${LOG_SERVER}/api/service-control`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service: 'synapse', action })
      });
      setTimeout(checkSynapseHealth, action === 'start' ? 3000 : 1000);
    } catch (e) {
      console.error('Failed to toggle Synapse:', e);
    }
    setTimeout(() => setRunningCommand(null), 500);
  };

  // Check MPC health (via log-server proxy)
  const checkMpcHealth = async () => {
    try {
      const res = await fetch(`${LOG_SERVER}/api/mpc/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(2000)
      });
      setMpcStatus(res.ok ? 'online' : 'offline');
    } catch {
      setMpcStatus('offline');
    }
  };

  // Toggle MPC service (via log-server proxy)
  const toggleMpc = async () => {
    setRunningCommand('mpc');
    try {
      const action = mpcStatus === 'online' ? 'stop' : 'start';
      await fetch(`${LOG_SERVER}/api/service-control`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service: 'mpc', action })
      });
      setTimeout(checkMpcHealth, action === 'start' ? 3000 : 1000);
    } catch (e) {
      console.error('Failed to toggle MPC:', e);
    }
    setTimeout(() => setRunningCommand(null), 500);
  };

  // Fetch pre-tool hook status and MPC status on mount
  useEffect(() => {
    fetch(`${LOG_SERVER}/api/hooks/pretool`)
      .then(r => r.json())
      .then(data => setPreToolEnabled(data.enabled))
      .catch(() => setPreToolEnabled(null));

    checkMpcHealth();
    checkSynapseHealth();
    const mpcInterval = setInterval(checkMpcHealth, 10000);
    const synapseInterval = setInterval(checkSynapseHealth, 15000);
    return () => {
      clearInterval(mpcInterval);
      clearInterval(synapseInterval);
    };
  }, []);

  const togglePreTool = async () => {
    const newState = !preToolEnabled;
    setRunningCommand('pretool');
    try {
      const res = await fetch(`${LOG_SERVER}/api/hooks/pretool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enable: newState })
      });
      const data = await res.json();
      if (data.success) {
        setPreToolEnabled(data.enabled);
      }
    } catch (e) {
      console.error('Failed to toggle pre-tool hook:', e);
    }
    setTimeout(() => setRunningCommand(null), 500);
  };

  const restartSystem = async () => {
    if (!confirm('Restart Overseer + Log Server?')) return;
    setSystemRestarting(true);
    setRunningCommand('restart');
    try {
      const res = await fetch(`${LOG_SERVER}/api/system/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      console.log('System restart:', data);
      setTimeout(() => window.location.reload(), 3000);
    } catch (e) {
      console.error('Failed to restart system:', e);
      setSystemRestarting(false);
    }
    setTimeout(() => setRunningCommand(null), 1000);
  };

  const ConnectionDot = ({ connected, label }: { connected: boolean; label: string }) => (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-black/20" title={label}>
      <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
      <span className="text-xs text-gray-400">{label}</span>
    </div>
  );

  const ServiceDot = ({ service }: { service: ServiceHealth }) => {
    const colors = {
      healthy: 'bg-green-400',
      degraded: 'bg-amber-400',
      down: 'bg-red-400 animate-pulse',
      unknown: 'bg-gray-400'
    };
    return (
      <div
        className="flex items-center gap-1.5 px-2 py-1 rounded bg-black/20"
        title={`${service.name}: ${service.status}${service.message ? ` - ${service.message}` : ''}`}
      >
        <div className={`w-2 h-2 rounded-full ${colors[service.status]}`} />
        <span className="text-xs text-gray-400">{service.name}</span>
      </div>
    );
  };

  const SystemMeter = ({ label, percent, threshold }: { label: string; percent: number; threshold: number }) => {
    const isHigh = percent >= threshold;
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-black/20" title={`${label}: ${percent}%`}>
        <div className="w-12 h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${isHigh ? 'bg-red-400' : percent >= threshold * 0.8 ? 'bg-amber-400' : 'bg-green-400'}`}
            style={{ width: `${Math.min(100, percent)}%` }}
          />
        </div>
        <span className={`text-xs ${isHigh ? 'text-red-400' : 'text-gray-400'}`}>{label}</span>
      </div>
    );
  };

  return (
    <div className="flex items-center justify-between px-2 sm:px-4 py-1.5 sm:py-2 bg-gray-900/90 border-b border-gray-800 backdrop-blur-sm">
      {/* Left: Connection Status + Health */}
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
        <div className="flex items-center gap-1 mr-1 sm:mr-2 shrink-0">
          <Zap className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-cyan-400" />
          <span className="text-xs sm:text-sm font-semibold text-white hidden sm:inline">Control Center</span>
        </div>

        {/* Connection dots - compact on mobile */}
        <ConnectionDot connected={wsConnected} label="Term" />
        <span className="hidden sm:inline"><ConnectionDot connected={sseConnected} label="SSE" /></span>
        <span className="hidden sm:inline"><ConnectionDot connected={supabaseConnected} label="DB" /></span>

        {/* Divider - hidden on mobile */}
        <div className="hidden md:block w-px h-4 bg-gray-700 mx-1" />

        {/* Service health - hidden on mobile */}
        <span className="hidden md:flex gap-1">
          {serviceHealth && Object.values(serviceHealth).map(service => (
            <ServiceDot key={service.name} service={service} />
          ))}
        </span>

        {/* Divider */}
        {systemHealth && <div className="hidden lg:block w-px h-4 bg-gray-700 mx-1" />}

        {/* System meters - hidden on mobile */}
        {systemHealth && (
          <span className="hidden lg:flex gap-1">
            <SystemMeter label="CPU" percent={systemHealth.cpu.percent} threshold={90} />
            <SystemMeter label="RAM" percent={systemHealth.memory.percent} threshold={80} />
            <SystemMeter label="Disk" percent={systemHealth.disk.percent} threshold={90} />
          </span>
        )}
      </div>

      {/* Right: Control Buttons - minimal on mobile */}
      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        {/* Zomboid Services - hidden on mobile */}
        {onOpenZomboidServices && (
          <button
            onClick={onOpenZomboidServices}
            className="hidden sm:flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-all text-green-400 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20"
            title="Zomboid Services - Start/Stop PZ server and PZRP"
          >
            <Gamepad2 className="w-3.5 h-3.5" />
            <span className="hidden md:inline">PZ</span>
          </button>
        )}

        {/* Process Manager - hidden on mobile */}
        {onOpenProcessManager && (
          <button
            onClick={onOpenProcessManager}
            className="hidden sm:flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-all text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20"
            title="Process Manager - View and kill processes"
          >
            <Cpu className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Procs</span>
          </button>
        )}

        {/* MPC (Prompt Compiler) Toggle */}
        <button
          onClick={toggleMpc}
          disabled={runningCommand === 'mpc' || mpcStatus === 'checking'}
          className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
            mpcStatus === 'online'
              ? 'text-purple-400 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30'
              : 'text-gray-400 bg-gray-500/10 hover:bg-gray-500/20 border border-gray-500/30'
          } disabled:opacity-50`}
          title={mpcStatus === 'online' ? 'MPC Online - click to stop' : 'MPC Offline - click to start'}
        >
          {runningCommand === 'mpc' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Brain className="w-3.5 h-3.5" />
          )}
          <span className={`w-1.5 h-1.5 rounded-full ${
            mpcStatus === 'online' ? 'bg-green-400' : mpcStatus === 'offline' ? 'bg-red-400' : 'bg-gray-400 animate-pulse'
          }`} />
          <span className="hidden sm:inline">MPC</span>
        </button>

        {/* Synapse Toggle */}
        <button
          onClick={toggleSynapse}
          disabled={runningCommand === 'synapse' || synapseStatus === 'checking'}
          className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
            synapseStatus === 'online'
              ? 'text-blue-400 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30'
              : 'text-gray-400 bg-gray-500/10 hover:bg-gray-500/20 border border-gray-500/30'
          } disabled:opacity-50`}
          title={synapseStatus === 'online' ? 'Synapse Online - click to stop' : 'Synapse Offline - click to start'}
        >
          {runningCommand === 'synapse' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Workflow className="w-3.5 h-3.5" />
          )}
          <span className={`w-1.5 h-1.5 rounded-full ${
            synapseStatus === 'online' ? 'bg-green-400' : synapseStatus === 'offline' ? 'bg-red-400' : 'bg-gray-400 animate-pulse'
          }`} />
          <span className="hidden sm:inline">Synapse</span>
        </button>

        {/* Pre-Tool Hook Toggle - icon only on mobile */}
        <button
          onClick={togglePreTool}
          disabled={runningCommand === 'pretool'}
          className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
            preToolEnabled
              ? 'text-emerald-400 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30'
              : 'text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30'
          } disabled:opacity-50`}
          title={preToolEnabled ? 'Pre-tool hook ENABLED - click to disable' : 'Pre-tool hook DISABLED - click to enable'}
        >
          {runningCommand === 'pretool' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : preToolEnabled ? (
            <Shield className="w-3.5 h-3.5" />
          ) : (
            <ShieldOff className="w-3.5 h-3.5" />
          )}
          <span className="hidden sm:inline">{preToolEnabled ? 'Pre-Tool' : 'Pre-Tool OFF'}</span>
        </button>

        {/* System Restart - hidden on mobile */}
        <button
          onClick={restartSystem}
          disabled={systemRestarting || runningCommand === 'restart'}
          className="hidden sm:flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-all text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 disabled:opacity-50"
          title="Restart Overseer + Log Server"
        >
          {runningCommand === 'restart' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RotateCcw className="w-3.5 h-3.5" />
          )}
          <span className="hidden md:inline">Restart</span>
        </button>

        {/* Reconnect */}
        <button
          onClick={onReconnect}
          className="p-1.5 sm:p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          title="Reconnect all"
        >
          <RefreshCw className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
        </button>
      </div>
    </div>
  );
};

export default StatusHeader;
